import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Fragment, useEffect, useState, useCallback } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Layout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Enhanced scroll to top function
  const scrollToTop = useCallback(() => {
    // Smooth scroll on desktop, instant on mobile
    if (window.innerWidth > 768) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  // Scroll to top and close mobile menu on route change
  useEffect(() => {
    scrollToTop();
    setIsMobileMenuOpen(false);
  }, [location.pathname, scrollToTop]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader 
        isMobileMenuOpen={isMobileMenuOpen} 
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
      <main className="flex-1 pt-16">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader({ isMobileMenuOpen, setIsMobileMenuOpen }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b border-slate-200 bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <NavLink to="/" className="flex items-center">
            <img 
              src="https://res.cloudinary.com/dd6ueb6xn/image/upload/v1755336892/crossdocs-logo-dark_legig2.png" 
              alt="CROSSDOCS Logo" 
              className="h-10 w-auto"
            />
          </NavLink>

          <nav className="hidden md:flex items-center gap-6 text-sm">
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/services">Services</NavLink>
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/industries">Industries</NavLink>
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/network">Network</NavLink>
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/pricing">Pricing</NavLink>
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/about">About</NavLink>
            <NavLink className={({isActive}) => classNames('hover:text-primary-700 transition-colors duration-200', isActive && 'text-primary-700 font-semibold')} to="/blog">Blog</NavLink>
            <NavLink to="/contact" className="ml-4 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-white shadow hover:bg-primary-700 transition-all duration-200">Contact</NavLink>
          </nav>
          <MobileMenu isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ isMobileMenuOpen, setIsMobileMenuOpen }) {
  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, [setIsMobileMenuOpen]);

  const toggleMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <div className="md:hidden">
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMenu}
        className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-600 transition-colors duration-200"
        aria-label="Toggle mobile menu"
      >
        {isMobileMenuOpen ? (
          <XMarkIcon className="h-6 w-6" />
        ) : (
          <Bars3Icon className="h-6 w-6" />
        )}
      </button>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-40"
            onClick={closeMenu}
          />
          
          {/* Menu Panel */}
          <div className="absolute top-16 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-lg">
            <div className="space-y-1 px-4 py-4">
              {[
                ['Services', '/services'],
                ['Industries', '/industries'],
                ['Network', '/network'],
                ['Pricing', '/pricing'],
                ['About', '/about'],
                ['Blog', '/blog'],
                ['Contact', '/contact'],
              ].map(([label, href]) => (
                <NavLink 
                  key={href} 
                  to={href} 
                  className="block rounded px-3 py-2 text-base font-medium hover:bg-slate-50 transition-colors duration-200"
                  onClick={closeMenu}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center">
              <img 
                src="https://res.cloudinary.com/dd6ueb6xn/image/upload/v1755336892/crossdocs-logo-dark_legig2.png" 
                alt="CROSSDOCS Logo" 
                className="h-10 w-auto"
              />
            </div>
            <p className="mt-4 text-sm text-slate-600 max-w-xs">Premium logistics and CROSSDOCS solutions engineered for speed, reliability and transparency.</p>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Company</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li><NavLink to="/about" className="hover:text-primary-700">About</NavLink></li>
              <li><NavLink to="/careers" className="hover:text-primary-700">Careers</NavLink></li>
              <li><NavLink to="/blog" className="hover:text-primary-700">Blog</NavLink></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Solutions</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li><NavLink to="/services" className="hover:text-primary-700">Services</NavLink></li>
              <li><NavLink to="/industries" className="hover:text-primary-700">Industries</NavLink></li>
              <li><NavLink to="/network" className="hover:text-primary-700">Network</NavLink></li>
              <li><NavLink to="/pricing" className="hover:text-primary-700">Pricing</NavLink></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900">Contact</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li>sales@crossdocs.co</li>
            </ul>
            <NavLink to="/contact" className="mt-4 inline-flex rounded-lg bg-primary-600 px-4 py-2 text-white shadow hover:bg-primary-700">Get a quote</NavLink>
          </div>
        </div>
        <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} CROSSDOCS. All rights reserved.</div>
      </div>
    </footer>
  );
}






